import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FileText,
  Plus,
  Edit2,
  Copy,
  Trash2,
  Check,
  Star,
  Palette,
  Type,
  Layout,
  Building2,
  Image,
  Settings2,
  Save,
  X,
  Eye,
  ChevronDown,
  ChevronUp,
  Upload
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const InvoiceTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [activeSection, setActiveSection] = useState('firm');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/invoice-templates/?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
      if (response.data.length > 0 && !selectedTemplate) {
        setSelectedTemplate(response.data[0]);
        setEditData(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedTemplate]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setEditData(template);
    setEditMode(false);
    setLogoPreview(null);
    setLogoFile(null);
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');

      // Upload logo first if there's a new one
      if (logoFile && selectedTemplate?.id) {
        const formData = new FormData();
        formData.append('file', logoFile);
        await axios.post(
          `${API_URL}/invoice-templates/${selectedTemplate.id}/upload-logo`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }

      if (selectedTemplate?.id) {
        // Update existing template
        await axios.put(
          `${API_URL}/invoice-templates/${selectedTemplate.id}`,
          editData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create new template
        await axios.post(
          `${API_URL}/invoice-templates/`,
          editData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      await fetchTemplates();
      setEditMode(false);
      setLogoFile(null);
      setLogoPreview(null);
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/invoice-templates/${template.id}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      alert('Failed to duplicate template');
    }
  };

  const handleSetDefault = async (template) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/invoice-templates/${template.id}/set-default`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchTemplates();
    } catch (error) {
      console.error('Error setting default template:', error);
      alert('Failed to set default template');
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/invoice-templates/${template.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchTemplates();
      if (selectedTemplate?.id === template.id) {
        setSelectedTemplate(null);
        setEditData({});
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handlePreview = async (template) => {
    if (!template?.id) return;
    setPreviewing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/invoice-templates/${template.id}/preview`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Error generating preview:', error);
      alert('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedTemplate(null);
    setEditData({
      name: 'New Template',
      is_default: false,
      is_active: true,
      firm_name: '',
      firm_address_line1: '',
      firm_address_line2: '',
      firm_city: '',
      firm_state: '',
      firm_zip: '',
      firm_phone: '',
      firm_email: '',
      firm_website: '',
      logo_width: 150,
      logo_height: 50,
      logo_position: 'left',
      primary_color: '#1a365d',
      secondary_color: '#2c5282',
      accent_color: '#4299e1',
      text_color: '#2d3748',
      background_color: '#ffffff',
      table_header_bg: '#2c5282',
      table_header_text: '#ffffff',
      table_row_alt_bg: '#f7fafc',
      header_font: 'Helvetica-Bold',
      body_font: 'Helvetica',
      header_font_size: 24,
      subheader_font_size: 12,
      body_font_size: 10,
      show_logo: true,
      show_firm_address: true,
      show_firm_phone: true,
      show_firm_email: true,
      show_firm_website: false,
      show_invoice_number: true,
      show_invoice_date: true,
      show_due_date: true,
      show_bill_to: true,
      show_payment_terms: true,
      show_notes: true,
      show_terms: true,
      invoice_title: 'INVOICE',
      description_header: 'Description',
      quantity_header: 'Quantity',
      rate_header: 'Unit Price',
      amount_header: 'Amount',
      payment_instructions: '',
      footer_text: '',
      default_terms: '',
      default_payment_terms: 'Due upon receipt',
      margin_top: 0.75,
      margin_bottom: 0.75,
      margin_left: 0.75,
      margin_right: 0.75
    });
    setEditMode(true);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const sections = [
    { id: 'firm', label: 'Firm Information', icon: Building2 },
    { id: 'logo', label: 'Logo Settings', icon: Image },
    { id: 'colors', label: 'Colors', icon: Palette },
    { id: 'fonts', label: 'Typography', icon: Type },
    { id: 'layout', label: 'Layout Options', icon: Layout },
    { id: 'content', label: 'Content & Labels', icon: FileText },
    { id: 'advanced', label: 'Advanced', icon: Settings2 }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900/30 to-slate-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-cyan-300">
          <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          Loading templates...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900/30 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
              <FileText size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Invoice Templates</h1>
              <p className="text-cyan-300/70">Design and customize your invoice appearance</p>
            </div>
          </div>
          <button
            onClick={handleCreateNew}
            className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition flex items-center gap-2 shadow-lg shadow-cyan-500/30"
          >
            <Plus size={18} />
            New Template
          </button>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Template List Sidebar */}
          <div className="col-span-3 bg-slate-800/50 backdrop-blur rounded-xl border border-cyan-500/20 p-4">
            <h2 className="text-lg font-semibold text-white mb-4">Templates</h2>
            <div className="space-y-2">
              {templates.map(template => (
                <div
                  key={template.id}
                  onClick={() => handleSelectTemplate(template)}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    selectedTemplate?.id === template.id
                      ? 'bg-gradient-to-r from-cyan-500/30 to-blue-500/30 border border-cyan-500/50'
                      : 'bg-slate-700/30 hover:bg-slate-700/50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">{template.name}</span>
                    {template.is_default && (
                      <Star size={14} className="text-yellow-400 fill-yellow-400" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {template.is_active ? (
                      <span className="text-xs text-green-400">Active</span>
                    ) : (
                      <span className="text-xs text-slate-500">Inactive</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Editor Panel */}
          <div className="col-span-9">
            {selectedTemplate || editData.name ? (
              <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-cyan-500/20 overflow-hidden">
                {/* Editor Header */}
                <div className="p-4 border-b border-cyan-500/20 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {editMode ? (
                      <input
                        type="text"
                        value={editData.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="bg-slate-700/50 border border-cyan-500/30 rounded-lg px-3 py-1.5 text-white text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                      />
                    ) : (
                      <h3 className="text-lg font-semibold text-white">{editData.name}</h3>
                    )}
                    {editData.is_default && (
                      <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">Default</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {editMode ? (
                      <>
                        <button
                          onClick={() => {
                            setEditMode(false);
                            setEditData(selectedTemplate || {});
                            setLogoFile(null);
                            setLogoPreview(null);
                          }}
                          className="px-3 py-1.5 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition flex items-center gap-2"
                        >
                          <X size={16} />
                          Cancel
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={saving}
                          className="px-3 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition flex items-center gap-2 disabled:opacity-50"
                        >
                          <Save size={16} />
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                      </>
                    ) : (
                      <>
                        {selectedTemplate && (
                          <button
                            onClick={() => handlePreview(selectedTemplate)}
                            disabled={previewing}
                            className="px-3 py-1.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:from-teal-600 hover:to-cyan-600 transition flex items-center gap-2 disabled:opacity-50"
                          >
                            <Eye size={16} />
                            {previewing ? 'Generating...' : 'Preview PDF'}
                          </button>
                        )}
                        <button
                          onClick={() => setEditMode(true)}
                          className="px-3 py-1.5 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition flex items-center gap-2"
                        >
                          <Edit2 size={16} />
                          Edit
                        </button>
                        {selectedTemplate && !selectedTemplate.is_default && (
                          <button
                            onClick={() => handleSetDefault(selectedTemplate)}
                            className="px-3 py-1.5 bg-slate-700/50 text-yellow-400 rounded-lg hover:bg-slate-700 transition flex items-center gap-2"
                          >
                            <Star size={16} />
                            Set Default
                          </button>
                        )}
                        {selectedTemplate && (
                          <button
                            onClick={() => handleDuplicate(selectedTemplate)}
                            className="px-3 py-1.5 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition flex items-center gap-2"
                          >
                            <Copy size={16} />
                            Duplicate
                          </button>
                        )}
                        {selectedTemplate && !selectedTemplate.is_default && (
                          <button
                            onClick={() => handleDelete(selectedTemplate)}
                            className="px-3 py-1.5 bg-slate-700/50 text-red-400 rounded-lg hover:bg-red-500/20 transition flex items-center gap-2"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Section Tabs */}
                <div className="flex border-b border-cyan-500/20 bg-slate-900/30">
                  {sections.map(section => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.id}
                        onClick={() => setActiveSection(section.id)}
                        className={`px-4 py-3 flex items-center gap-2 text-sm font-medium transition-all ${
                          activeSection === section.id
                            ? 'text-cyan-300 border-b-2 border-cyan-500 bg-cyan-500/10'
                            : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
                        }`}
                      >
                        <Icon size={16} />
                        {section.label}
                      </button>
                    );
                  })}
                </div>

                {/* Section Content */}
                <div className="p-6">
                  {activeSection === 'firm' && (
                    <FirmSection editData={editData} editMode={editMode} onChange={handleInputChange} />
                  )}
                  {activeSection === 'logo' && (
                    <LogoSection
                      editData={editData}
                      editMode={editMode}
                      onChange={handleInputChange}
                      logoPreview={logoPreview}
                      onLogoChange={handleLogoChange}
                    />
                  )}
                  {activeSection === 'colors' && (
                    <ColorsSection editData={editData} editMode={editMode} onChange={handleInputChange} />
                  )}
                  {activeSection === 'fonts' && (
                    <FontsSection editData={editData} editMode={editMode} onChange={handleInputChange} />
                  )}
                  {activeSection === 'layout' && (
                    <LayoutSection editData={editData} editMode={editMode} onChange={handleInputChange} />
                  )}
                  {activeSection === 'content' && (
                    <ContentSection editData={editData} editMode={editMode} onChange={handleInputChange} />
                  )}
                  {activeSection === 'advanced' && (
                    <AdvancedSection editData={editData} editMode={editMode} onChange={handleInputChange} />
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-cyan-500/20 p-12 text-center">
                <FileText size={48} className="text-cyan-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Template Selected</h3>
                <p className="text-slate-400 mb-4">Select a template from the sidebar or create a new one</p>
                <button
                  onClick={handleCreateNew}
                  className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition inline-flex items-center gap-2"
                >
                  <Plus size={18} />
                  Create Template
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Section Components
const FormField = ({ label, value, onChange, type = 'text', disabled, options, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-cyan-300 mb-1">{label}</label>
    {type === 'select' ? (
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full bg-slate-700/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50 disabled:bg-slate-800/50 disabled:text-slate-400"
      >
        {options?.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    ) : type === 'textarea' ? (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        rows={3}
        className="w-full bg-slate-700/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50 disabled:bg-slate-800/50 disabled:text-slate-400"
      />
    ) : type === 'color' ? (
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-10 h-10 rounded cursor-pointer disabled:cursor-not-allowed"
        />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="flex-1 bg-slate-700/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50 disabled:bg-slate-800/50 disabled:text-slate-400"
        />
      </div>
    ) : (
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full bg-slate-700/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50 disabled:bg-slate-800/50 disabled:text-slate-400"
      />
    )}
  </div>
);

const ToggleField = ({ label, description, value, onChange, disabled }) => (
  <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
    <div>
      <span className="text-white font-medium">{label}</span>
      {description && <p className="text-sm text-slate-400">{description}</p>}
    </div>
    <button
      onClick={() => !disabled && onChange(!value)}
      disabled={disabled}
      className={`w-12 h-6 rounded-full transition-colors ${
        value ? 'bg-cyan-500' : 'bg-slate-600'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
        value ? 'translate-x-6' : 'translate-x-0.5'
      }`} />
    </button>
  </div>
);

const FirmSection = ({ editData, editMode, onChange }) => (
  <div className="grid grid-cols-2 gap-4">
    <div className="col-span-2">
      <FormField label="Firm Name" value={editData.firm_name} onChange={(v) => onChange('firm_name', v)} disabled={!editMode} />
    </div>
    <FormField label="Address Line 1" value={editData.firm_address_line1} onChange={(v) => onChange('firm_address_line1', v)} disabled={!editMode} />
    <FormField label="Address Line 2" value={editData.firm_address_line2} onChange={(v) => onChange('firm_address_line2', v)} disabled={!editMode} />
    <FormField label="City" value={editData.firm_city} onChange={(v) => onChange('firm_city', v)} disabled={!editMode} />
    <div className="grid grid-cols-2 gap-4">
      <FormField label="State" value={editData.firm_state} onChange={(v) => onChange('firm_state', v)} disabled={!editMode} />
      <FormField label="ZIP" value={editData.firm_zip} onChange={(v) => onChange('firm_zip', v)} disabled={!editMode} />
    </div>
    <FormField label="Phone" value={editData.firm_phone} onChange={(v) => onChange('firm_phone', v)} disabled={!editMode} />
    <FormField label="Email" value={editData.firm_email} onChange={(v) => onChange('firm_email', v)} disabled={!editMode} />
    <div className="col-span-2">
      <FormField label="Website" value={editData.firm_website} onChange={(v) => onChange('firm_website', v)} disabled={!editMode} />
    </div>
  </div>
);

const LogoSection = ({ editData, editMode, onChange, logoPreview, onLogoChange }) => (
  <div className="space-y-6">
    <div className="flex items-start gap-6">
      <div className="flex-1">
        <label className="block text-sm font-medium text-cyan-300 mb-2">Logo Image</label>
        {editMode && (
          <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-cyan-500/30 rounded-lg cursor-pointer hover:border-cyan-500/50 transition">
            <input type="file" accept="image/*" onChange={onLogoChange} className="hidden" />
            <div className="text-center">
              <Upload size={24} className="text-cyan-400 mx-auto mb-2" />
              <span className="text-sm text-cyan-300">Click to upload logo</span>
            </div>
          </label>
        )}
        {(logoPreview || editData.logo_path) && (
          <div className="mt-4 p-4 bg-slate-700/30 rounded-lg">
            <img
              src={logoPreview || `${API_URL}/files/${editData.logo_path}`}
              alt="Logo Preview"
              className="max-h-20 object-contain"
            />
          </div>
        )}
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4">
      <FormField label="Logo Width (px)" type="number" value={editData.logo_width} onChange={(v) => onChange('logo_width', v)} disabled={!editMode} />
      <FormField label="Logo Height (px)" type="number" value={editData.logo_height} onChange={(v) => onChange('logo_height', v)} disabled={!editMode} />
      <FormField
        label="Position"
        type="select"
        value={editData.logo_position}
        onChange={(v) => onChange('logo_position', v)}
        disabled={!editMode}
        options={[
          { value: 'left', label: 'Left' },
          { value: 'center', label: 'Center' },
          { value: 'right', label: 'Right' }
        ]}
      />
    </div>
  </div>
);

const ColorsSection = ({ editData, editMode, onChange }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 gap-4">
      <FormField label="Primary Color" type="color" value={editData.primary_color} onChange={(v) => onChange('primary_color', v)} disabled={!editMode} />
      <FormField label="Secondary Color" type="color" value={editData.secondary_color} onChange={(v) => onChange('secondary_color', v)} disabled={!editMode} />
      <FormField label="Accent Color" type="color" value={editData.accent_color} onChange={(v) => onChange('accent_color', v)} disabled={!editMode} />
      <FormField label="Text Color" type="color" value={editData.text_color} onChange={(v) => onChange('text_color', v)} disabled={!editMode} />
      <FormField label="Background Color" type="color" value={editData.background_color} onChange={(v) => onChange('background_color', v)} disabled={!editMode} />
    </div>
    <div className="border-t border-cyan-500/20 pt-4">
      <h4 className="text-white font-medium mb-4">Table Colors</h4>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="Header Background" type="color" value={editData.table_header_bg} onChange={(v) => onChange('table_header_bg', v)} disabled={!editMode} />
        <FormField label="Header Text" type="color" value={editData.table_header_text} onChange={(v) => onChange('table_header_text', v)} disabled={!editMode} />
        <FormField label="Alternating Row" type="color" value={editData.table_row_alt_bg} onChange={(v) => onChange('table_row_alt_bg', v)} disabled={!editMode} />
      </div>
    </div>
  </div>
);

const FontsSection = ({ editData, editMode, onChange }) => {
  const fontOptions = [
    { value: 'Helvetica', label: 'Helvetica' },
    { value: 'Helvetica-Bold', label: 'Helvetica Bold' },
    { value: 'Times-Roman', label: 'Times Roman' },
    { value: 'Times-Bold', label: 'Times Bold' },
    { value: 'Courier', label: 'Courier' },
    { value: 'Courier-Bold', label: 'Courier Bold' }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Header Font" type="select" value={editData.header_font} onChange={(v) => onChange('header_font', v)} disabled={!editMode} options={fontOptions} />
        <FormField label="Body Font" type="select" value={editData.body_font} onChange={(v) => onChange('body_font', v)} disabled={!editMode} options={fontOptions} />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="Header Size (pt)" type="number" value={editData.header_font_size} onChange={(v) => onChange('header_font_size', v)} disabled={!editMode} />
        <FormField label="Subheader Size (pt)" type="number" value={editData.subheader_font_size} onChange={(v) => onChange('subheader_font_size', v)} disabled={!editMode} />
        <FormField label="Body Size (pt)" type="number" value={editData.body_font_size} onChange={(v) => onChange('body_font_size', v)} disabled={!editMode} />
      </div>
    </div>
  );
};

const LayoutSection = ({ editData, editMode, onChange }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <ToggleField label="Show Logo" value={editData.show_logo} onChange={(v) => onChange('show_logo', v)} disabled={!editMode} />
      <ToggleField label="Show Firm Address" value={editData.show_firm_address} onChange={(v) => onChange('show_firm_address', v)} disabled={!editMode} />
      <ToggleField label="Show Firm Phone" value={editData.show_firm_phone} onChange={(v) => onChange('show_firm_phone', v)} disabled={!editMode} />
      <ToggleField label="Show Firm Email" value={editData.show_firm_email} onChange={(v) => onChange('show_firm_email', v)} disabled={!editMode} />
      <ToggleField label="Show Firm Website" value={editData.show_firm_website} onChange={(v) => onChange('show_firm_website', v)} disabled={!editMode} />
      <ToggleField label="Show Invoice Number" value={editData.show_invoice_number} onChange={(v) => onChange('show_invoice_number', v)} disabled={!editMode} />
      <ToggleField label="Show Invoice Date" value={editData.show_invoice_date} onChange={(v) => onChange('show_invoice_date', v)} disabled={!editMode} />
      <ToggleField label="Show Due Date" value={editData.show_due_date} onChange={(v) => onChange('show_due_date', v)} disabled={!editMode} />
      <ToggleField label="Show Bill To Section" value={editData.show_bill_to} onChange={(v) => onChange('show_bill_to', v)} disabled={!editMode} />
      <ToggleField label="Show Payment Terms" value={editData.show_payment_terms} onChange={(v) => onChange('show_payment_terms', v)} disabled={!editMode} />
      <ToggleField label="Show Notes" value={editData.show_notes} onChange={(v) => onChange('show_notes', v)} disabled={!editMode} />
      <ToggleField label="Show Terms & Conditions" value={editData.show_terms} onChange={(v) => onChange('show_terms', v)} disabled={!editMode} />
    </div>
  </div>
);

const ContentSection = ({ editData, editMode, onChange }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 gap-4">
      <FormField label="Invoice Title" value={editData.invoice_title} onChange={(v) => onChange('invoice_title', v)} disabled={!editMode} />
      <FormField label="Default Payment Terms" value={editData.default_payment_terms} onChange={(v) => onChange('default_payment_terms', v)} disabled={!editMode} />
    </div>
    <div className="border-t border-cyan-500/20 pt-4">
      <h4 className="text-white font-medium mb-4">Table Column Headers</h4>
      <div className="grid grid-cols-4 gap-4">
        <FormField label="Description" value={editData.description_header} onChange={(v) => onChange('description_header', v)} disabled={!editMode} />
        <FormField label="Quantity" value={editData.quantity_header} onChange={(v) => onChange('quantity_header', v)} disabled={!editMode} />
        <FormField label="Rate" value={editData.rate_header} onChange={(v) => onChange('rate_header', v)} disabled={!editMode} />
        <FormField label="Amount" value={editData.amount_header} onChange={(v) => onChange('amount_header', v)} disabled={!editMode} />
      </div>
    </div>
    <div className="border-t border-cyan-500/20 pt-4 space-y-4">
      <FormField label="Payment Instructions" type="textarea" value={editData.payment_instructions} onChange={(v) => onChange('payment_instructions', v)} disabled={!editMode} placeholder="Instructions for how to pay..." />
      <FormField label="Footer Text" type="textarea" value={editData.footer_text} onChange={(v) => onChange('footer_text', v)} disabled={!editMode} placeholder="Text to appear at the bottom of the invoice..." />
      <FormField label="Default Terms & Conditions" type="textarea" value={editData.default_terms} onChange={(v) => onChange('default_terms', v)} disabled={!editMode} placeholder="Standard terms and conditions..." />
    </div>
  </div>
);

const AdvancedSection = ({ editData, editMode, onChange }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-4 gap-4">
      <FormField label="Top Margin (in)" type="number" value={editData.margin_top} onChange={(v) => onChange('margin_top', v)} disabled={!editMode} />
      <FormField label="Bottom Margin (in)" type="number" value={editData.margin_bottom} onChange={(v) => onChange('margin_bottom', v)} disabled={!editMode} />
      <FormField label="Left Margin (in)" type="number" value={editData.margin_left} onChange={(v) => onChange('margin_left', v)} disabled={!editMode} />
      <FormField label="Right Margin (in)" type="number" value={editData.margin_right} onChange={(v) => onChange('margin_right', v)} disabled={!editMode} />
    </div>
    <div className="border-t border-cyan-500/20 pt-4">
      <ToggleField label="Template Active" description="Inactive templates won't appear in the selection list" value={editData.is_active} onChange={(v) => onChange('is_active', v)} disabled={!editMode} />
    </div>
  </div>
);

export default InvoiceTemplates;
